import Agent from "core/services/api";
import Configuration from "Configuration";
import { Project, Result, PlanKey } from "core/models";

export interface Auth {
    redirecturl: string;
    token: string;
    tokensecret: string;
}
export interface PublisherConfig {
    key: string;
    name: string;
    publisherKey: string;
}

export interface KijkGenre {
    id: number;
    name: string;
}
export interface KijkEpisode {
    type: "EPISODE" | "SERIES";
    guid: string;
    seasonNumber: number;
    seriesepisodenumber: number;
    tvseasonepisodenumber: number;
    seriestitle: string;
    seriesguid: string;
    lastpublishdate: string;
    title?: string;
}

const listProjects = async (
    accessToken: string,
    items: number,
): Promise<Result<Project[], string>> => {
    const request = {
        enabledOnly: true,
        channelId: 0,
        orderFields: ["insertionDate", false],
        maxCount: items,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<Project[]>(
        "project/list/v1",
        request,
        headers,
        "site",
    );

    if (!result.resultObject && result.messages) {
        return {
            value: undefined,
            error: "Something went wrong while trying to fetch your projects",
        };
    }

    return {
        value: result.resultObject,
        error: undefined,
    };
};
const getProject = async (
    accessToken: string,
    projectId: number,
): Promise<Result<Project, string>> => {
    const request = {
        enabledOnly: true,
        id: projectId,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<Project>(
        "project/get/v1",
        request,
        headers,
        "site",
    );

    if (!result.resultObject && result.messages) {
        return {
            value: undefined,
            error: "Something went wrong while trying to fetch the project",
        };
    }

    return {
        value: result.resultObject,
        error: undefined,
    };
};

const configpublishsetting = async (
    accessToken: string,
    projectID: number,
    publishSettingKey: string,
    operation = "startauthentication",
    request = {},
): Promise<Result<string, string>> => {
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<string>(
        `project/configpublishsetting/v1/${projectID}/${publishSettingKey}/${operation}`,
        request,
        headers,
        "site",
    );
    if (result.messages) {
        return {
            value: undefined,
            error: "Unable to configpublishsetting",
        };
    }
    return {
        value: result.resultObject,
        error: undefined,
    };
};

export interface PublishSettingsParams {
    publishSettingKey: string;
    operation: string | null;
    parameters: string;
}
export interface CheckConfigPublishSettingResult {
    operation: string;
    publishSettingKey: string;
    result: boolean;
    resultValue: string;
}
const configMultiplePublishsettings = async (
    accessToken: string,
    projectID: number,
    settingsArr: PublishSettingsParams[],
    request = {
        id: projectID,
        publishSettingParams: settingsArr,
    },
): Promise<Result<CheckConfigPublishSettingResult[], string>> => {
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<CheckConfigPublishSettingResult[]>(
        `project/configmultiplepublishsettings/v1`,
        request,
        headers,
        "site",
    );
    if (result.messages) {
        return {
            value: undefined,
            error: "Unable to configmultiplepublishsettings",
        };
    }
    return {
        value: result.resultObject,
        error: undefined,
    };
};

async function listPublishers(
    accessToken: string,
): Promise<Result<Project[], string>> {
    const request = {
        enabledOnly: true,
        channelId: 0,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<Project[]>(
        "project/listpublishers/v1",
        request,
        headers,
        "site",
    );

    if (!result.resultObject && result.messages) {
        return {
            value: undefined,
            error: "Something went wrong while trying to fetch your publishers",
        };
    }

    return {
        value: result.resultObject,
        error: undefined,
    };
}

const createSocialSetting = async (
    accessToken: string,
    projectID: number,
    publisherKey: string,
    publishSettingName: string,
    publishSettingKey: string,
    parameters = {},
): Promise<Result<PublisherConfig, string>> => {
    const headers = new Headers();
    headers.append("Authorization", accessToken);

    const request = {
        id: projectID,
        publisherKey,
        publishSettingKey,
        publishSettingName,
        baseSettingsUseDefaults: true,
        configUseDefaults: true,
        parameters: JSON.stringify(parameters),
    };

    const result = await Agent.post<PublisherConfig>(
        `project/createinitconfigpublishsetting/v1/`,
        request,
        headers,
        "site",
    );
    if (result.messages) {
        return {
            value: undefined,
            error: "Unable to create new publishsetting",
        };
    }
    return {
        value: result.resultObject,
        error: undefined,
    };
};

const createPublishsetting = async (
    accessToken: string,
    projectID: number,
    publisherKey: string,
    publishSettingName: string,
    publishSettingKey: string,
    parameters = {},
): Promise<Result<PublisherConfig, string>> => {
    const headers = new Headers();
    headers.append("Authorization", accessToken);

    const request = {
        id: projectID,
        publisherKey,
        publishSettingKey,
        publishSettingName,
        baseSettingsUseDefaults: false,
        jsonOutput: false,
        mediaFileXslKey: "default_xsl",
        metaVariantDefaultFormatKeys: null,
        configUseDefaults: false,
        parameters: JSON.stringify(parameters),
    };

    const result = await Agent.post<PublisherConfig>(
        `project/createinitconfigpublishsetting/v1/`,
        request,
        headers,
        "site",
    );
    if (result.messages) {
        return {
            value: undefined,
            error: "Unable to create new publishsetting",
        };
    }
    return {
        value: result.resultObject,
        error: undefined,
    };
};

const updatePublishsetting = async (
    accessToken: string,
    projectID: number,
    publishSettingKey: string,
    publishSettingName: string,
    parameters = {},
): Promise<Result<PublisherConfig, string>> => {
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const body = {
        id: projectID,
        publishSettingKey,
        publishSettingName,
        updateBaseSettings: false,
        updateBaseSettingsUseDefaults: false,
        jsonOutput: false,
        mediaFileXslKey: null,
        metaVariantDefaultFormatKeys: null,
        updateConfig: true,
        updateConfigUseDefaults: false,
        parameters: JSON.stringify(parameters),
    };
    const result = await Agent.post<PublisherConfig>(
        `project/initconfigpublishsetting/v1`,
        body,
        headers,
        "site",
    );
    if (result.messages) {
        return {
            value: undefined,
            error: "Unable to update the publishsetting",
        };
    }
    return {
        value: result.resultObject,
        error: undefined,
    };
};

const deletePublishsetting = async (
    accessToken: string,
    projectID: number,
    publishSettingKey: string,
): Promise<Result<boolean, string>> => {
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const request = {
        id: projectID,
        publishSettingKey,
    };
    const result = await Agent.post<boolean>(
        `project/deletepublishsetting/v1/`,
        request,
        headers,
        "site",
    );
    if (result.messages) {
        return {
            value: undefined,
            error: "Unable to delete publishsetting",
        };
    }
    return {
        value: true,
        error: undefined,
    };
};

const getKijkGenres = async (
    accessToken: string,
    publishSettingKey: string,
    projectId: number,
): Promise<Result<KijkGenre[], string>> => {
    const request = {};
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<string>(
        `/project/preparepublish/v1/${projectId}/${publishSettingKey}/listskogenres`,
        request,
        headers,
        "site",
    );

    if (!result.resultObject) {
        return {
            value: undefined,
            error: "could not get kijk genres",
        };
    }

    return {
        value: JSON.parse(result.resultObject),
        error: undefined,
    };
};

const getKijkEpisodes = async (
    accessToken: string,
    publishSettingKey: string,
    type: ("EPISODE" | "SERIES")[] = ["EPISODE", "SERIES"],
    query = "",
    guid = "",
    projectId = 0,
): Promise<Result<KijkEpisode[], string>> => {
    const request = {
        programtypes: type,
        guid,
        searchparam: query,
        skip: 0,
        limit: 40,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<string>(
        `/project/preparepublish/v1/${projectId}/${publishSettingKey}/listprograms`,
        request,
        headers,
        "site",
    );

    if (!result.resultObject) {
        return {
            value: undefined,
            error: "could not get kijk genres",
        };
    }

    return {
        value: JSON.parse(result.resultObject),
        error: undefined,
    };
};

const requestUpdatePackage = async (
    accessToken: string,
    projectID: number,
    packageKey: PlanKey,
): Promise<Result<boolean, string>> => {
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const BASE_URL = Configuration.get("baseURL");
    const request = {
        id: projectID,
        package: packageKey,
        baseUrl: BASE_URL,
    };
    const result = await Agent.post<string>(
        `project/requestupdatepackage/v1`,
        request,
        headers,
        "site",
    );
    if (result.messages) {
        return {
            value: undefined,
            error: "Unable to update package",
        };
    }
    return {
        value: true,
        error: undefined,
    };
};

export {
    listProjects,
    configpublishsetting,
    configMultiplePublishsettings,
    getProject,
    listPublishers,
    createSocialSetting,
    createPublishsetting,
    deletePublishsetting,
    getKijkEpisodes,
    getKijkGenres,
    updatePublishsetting,
    requestUpdatePackage,
};
